// ============================================================================
// FSA STATEMENTS SERVICE  (FSA-2.2 Wave 3 Statement save)
// ============================================================================

import { get, put, post } from '../../client/axiosClient';
import { fsaStatementsEndpoints } from '../../endpoints/fsa/fsaStatements.endpoints';
import type { ApiResponse, ID } from '../../types';
import type {
  EngagementStatementData,
  StatementListItem,
  StatementHydrate,
  SaveStatementDataRequest,
  ExcludeStatementRequest,
} from '../../types/fsa/statementData.types';

export interface StatementTransitionRequest {
  rowVersion?: number | null;
}

class FsaStatementsService {
  async list(engagementId: ID): Promise<ApiResponse<StatementListItem[]>> {
    const r = await get<ApiResponse<StatementListItem[]>>(
      fsaStatementsEndpoints.listByEngagement(engagementId),
    );
    return r.data;
  }

  async hydrate(engagementId: ID, reportId: ID): Promise<ApiResponse<StatementHydrate>> {
    const r = await get<ApiResponse<StatementHydrate>>(
      fsaStatementsEndpoints.hydrate(engagementId, reportId),
    );
    return r.data;
  }

  async save(
    engagementId: ID,
    reportId: ID,
    request: SaveStatementDataRequest,
  ): Promise<ApiResponse<EngagementStatementData>> {
    const r = await put<ApiResponse<EngagementStatementData>>(
      fsaStatementsEndpoints.save(engagementId, reportId), request,
    );
    return r.data;
  }

  async submit(
    engagementId: ID, reportId: ID, request: StatementTransitionRequest = {},
  ): Promise<ApiResponse<EngagementStatementData>> {
    const r = await post<ApiResponse<EngagementStatementData>>(
      fsaStatementsEndpoints.submit(engagementId, reportId), request,
    );
    return r.data;
  }

  async approve(
    engagementId: ID, reportId: ID, request: StatementTransitionRequest = {},
  ): Promise<ApiResponse<EngagementStatementData>> {
    const r = await post<ApiResponse<EngagementStatementData>>(
      fsaStatementsEndpoints.approve(engagementId, reportId), request,
    );
    return r.data;
  }

  async reopen(
    engagementId: ID, reportId: ID, request: StatementTransitionRequest = {},
  ): Promise<ApiResponse<EngagementStatementData>> {
    const r = await post<ApiResponse<EngagementStatementData>>(
      fsaStatementsEndpoints.reopen(engagementId, reportId), request,
    );
    return r.data;
  }

  /**
   * FSA-2.4 CF5 — bulk-import PY values from the prior engagement's
   * saved Cash Flow. Backend looks up settings.priorEngagementId,
   * loads its saved CF, and copies amount_actual → prior_amount_actual
   * for every matching VALUE line and subsection item id.
   */
  async importPriorPy(
    engagementId: ID, reportId: ID,
  ): Promise<ApiResponse<EngagementStatementData>> {
    const r = await post<ApiResponse<EngagementStatementData>>(
      fsaStatementsEndpoints.importPriorPy(engagementId, reportId), {},
    );
    return r.data;
  }

  /** FSA-2.2 — mark a Statement as not-applicable. Returns 409 on saved-data conflict. */
  async exclude(
    engagementId: ID,
    reportId: ID,
    request: ExcludeStatementRequest,
  ): Promise<ApiResponse<EngagementStatementData>> {
    const r = await post<ApiResponse<EngagementStatementData>>(
      fsaStatementsEndpoints.exclude(engagementId, reportId), request,
    );
    return r.data;
  }

  async include(
    engagementId: ID,
    reportId: ID,
  ): Promise<ApiResponse<EngagementStatementData>> {
    const r = await post<ApiResponse<EngagementStatementData>>(
      fsaStatementsEndpoints.include(engagementId, reportId), {},
    );
    return r.data;
  }

  /**
   * Server-rendered Statement preview PDF (BS / PL / CF). Returns a raw
   * Blob (Content-Type: application/pdf) for inline iframe rendering via
   * URL.createObjectURL — mirrors the FsaTemplateService preview pattern.
   * Reads strictly from saved Statement data; backend returns 400 when
   * the Statement has not been saved yet.
   */
  async getPreviewPdfBlob(
    engagementId: ID,
    reportId: ID,
    mode: 'actual' | 'rounded' = 'actual',
  ): Promise<Blob> {
    const response = await get<Blob>(
      fsaStatementsEndpoints.previewPdf(engagementId, reportId, mode),
      { responseType: 'blob' },
    );
    return response.data;
  }
}

export const fsaStatementsService = new FsaStatementsService();
export default fsaStatementsService;
